import React, { createContext, useContext, useState, useMemo, useEffect } from "react";

// Shared gender filter state — populated by the Header pill toggle and
// consumed across the storefront (Hero strip, Featured Categories, Trending
// Now, Hot Deals, Use Cases, Testimonials, How It Works, CTA Strip, and the
// /shop catalog page). Values: "men" | "women" | "couple".
//
// PERSISTENCE: the active gender is mirrored to localStorage so that:
//   1. Hard refresh keeps the user on the same gender view.
//   2. Closing the tab + reopening the site lands on the last picked gender.
//   3. Switching tabs and coming back doesn't reset the filter.
// We use a `try/catch` because some embedded browsers (in-app webviews) throw
// SecurityError when localStorage is disabled — in that case we silently
// fall back to in-memory state without breaking the app.

const STORAGE_KEY = "xyz_gender_v1";
const VALID = new Set(["men", "women", "couple"]);

function readPersisted() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && VALID.has(v)) return v;
  } catch (_e) {
    // localStorage disabled — fall back to default
  }
  return "men";
}

const GenderContext = createContext({
  gender: "men",
  setGender: () => {},
});

export function GenderProvider({ children }) {
  const [gender, setGenderState] = useState(readPersisted);

  // Write-through on every change. Wrapping setter so consumers don't need
  // to know about the storage mechanism.
  const setGender = (next) => {
    if (!VALID.has(next)) return;
    setGenderState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_e) {
      // localStorage write blocked — state still updates in memory
    }
  };

  // Cross-tab sync — if the user has the storefront open in two tabs and
  // changes gender in one, the other tab updates without a refresh.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && VALID.has(e.newValue) && e.newValue !== gender) {
        setGenderState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [gender]);

  const value = useMemo(() => ({ gender, setGender }), [gender]);
  return <GenderContext.Provider value={value}>{children}</GenderContext.Provider>;
}

export function useGender() {
  return useContext(GenderContext);
}
